<template>
  <v-dialog v-model="displayForm" persistent max-width="1240px">
    <v-form ref="form" v-model="valid" lazy-validation v-if="displayForm">
      <v-card>
        <v-card-title>
          {{ model.id ? 'Update Wood' : 'Add Wood' }}
        </v-card-title>
        <v-card-text>
          <v-alert
            :value="msg"
            type="error"
            dismissible
            transition="scale-transition"
            v-for="msg in message"
            :key="msg"
          >{{ msg }}</v-alert>
          <v-row>
            <v-col cols="2">
              <v-checkbox
                v-model="model.headOnly"
                label="Head Only"
                :false-value="0"
                :true-value="1"
              ></v-checkbox>
            </v-col>
            <v-col cols="2">
              <v-checkbox v-model="model.sst" label="SST Pure" :false-value="0" :true-value="1"></v-checkbox>
            </v-col>
            <v-col cols="4">
              <v-text-field v-model="model.quantity" type="number" label="Quantity" :rules="mandatoryRules"></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select
                :items="types"
                label="Type"
                v-model="model.typeId"
                :rules="mandatoryRules"
              ></v-select>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="6">
              <product-selection
                  v-model="model.headProductId"
                  label="Club Head"
                  :dominantHand="dominantHand"
                  :type="headType"
                  :catalogId="catalogId"
                  :rules="mandatoryRules"
                  :currency="currency"
              ></product-selection>
            </v-col>
            <v-col cols="3">
              <v-text-field
                label="Setting 1"
                v-model="model.headSetting"
                :rules="[rules.notHeadOnlyRequired]"
                :disabled="!model.headProductId"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-text-field
                v-model="model.headSetting2"
                label="Setting 2"
                :disabled="!model.headProductId"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row v-if="!model.headOnly">
              <v-col cols="6">
                <product-selection
                  v-model="model.shaftProductId"
                  label="Shaft"
                  :type="shaftType"
                  :catalogId="catalogId"
                  :rules="[rules.notHeadOnlyRequired]"
                  :currency="currency"
                ></product-selection>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="model.shaftLength"
                  label="Length"
                  :rules="[rules.notHeadOnlyRequired]"
                  :disabled="!model.shaftProductId"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-text-field
                  v-model="model.shaftCpm"
                  label="CPM"
                  :rules="[rules.notHeadOnlyRequired]"
                  :disabled="!model.shaftProductId"
                ></v-text-field>
              </v-col>
              <v-col cols="2">
                <v-combobox
                  label="SW"
                  v-model="model.shaftSW"
                  :items="[
                    'A0','A1','A2','A3','A4','A5','A6','A7','A8','A9',
                    'B0','B1','B2','B3','B4','B5','B6','B7','B8','B9',
                    'C0','C1','C2','C3','C4','C5','C6','C7','C8','C9',
                    'D0','D1','D2','D3','D4','D5','D6','D7','D8','D9',
                    'E0','E1','E2','E3','E4','E5','E6','E7','E8','E9',
                    'F0','F1','F2','F3','F4','F5','F6','F7','F8','F9',
                  ]"
                  :rules="[rules.notHeadOnlyRequired]"
                  :disabled="!model.shaftProductId"
                />
              </v-col>
            </v-row>
          <v-row v-if="!model.headOnly">
              <v-col cols="6">
                <product-selection
                  v-model="model.gripProductId"
                  label="Grip"
                  :type="['GRIP']"
                  :catalogId="catalogId"
                  :rules="[rules.notHeadOnlyRequired]"
                  :currency="currency"
                ></product-selection>
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="model.gripExtraWraps"
                  label="Extra Wraps"
                  :disabled="!model.gripProductId"
                ></v-text-field>
              </v-col>
              <v-col cols="3">
                <v-select
                  :items="logo"
                  label="Logo"
                  v-model="model.gripLogo"
                  :disabled="!model.gripProductId"
                ></v-select>
              </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="cancel()">Cancel</v-btn>
          <v-btn color="blue darken-1" text @click="add()" v-if="!model.id">Add</v-btn>
          <v-btn color="blue darken-1" text @click="update()" v-else>Update</v-btn>
        </v-card-actions>
      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import ProductSelection from "@components/forms/search/ProductSelection.vue"

const defaultModel = {
  sst: 0,
  headOnly: 0,
  quantity: 1,
  typeId: null,
  headProductId: null,
  headSetting: null,
  headSetting2: null,
  shaftProductId: null,
  shaftLength: null,
  shaftCpm: null,
  shaftSW: null,
  gripProductId: null,
  gripExtraWraps: null,
  gripLogo: 'up'
}

export default {
  components: {
    ProductSelection
  },
  props: {
    prescriptionId: {
      type: Number,
      required: true
    },
    dominantHand: {
      type: String,
      default: () => ''
    },
    catalogId: {
      type: Number,
      required: true
    },
    currency:{
      type: String,
      default: () => 'EUR'
    }
  },
  data: () => ({
    loading: false,
    model: {},
    types: [
      { text: "Driver", value: "driver" },
      { text: "Fairway", value: "fairway" },
      { text: "Hybrid/Utility", value: "hybrid" }
    ],
    logo: [
      { text: "UP", value: "up" },
      { text: "DOWN", value: "dw" }
    ],
    mandatoryRules: [v => !!v || "This fiels is mandatory"],
    valid: null,
    message: null,
    displayForm: false
  }),
  computed: {
    headType () {
      switch (this.model.typeId) {
        case "driver":
          return ['DRIVER','ADAPTOR TIPS'];
        case "fairway":
          return ['FAIRWAY','ADAPTOR TIPS'];
        case "hybrid":
          return ['UTILITY','HYBRID','UTILITY IRON','ADAPTOR TIPS'];
      }
      return [];
    },
    shaftType: function() {
      switch (this.model.typeId) {
        case "driver":
          return ['WOOD SHAFT', 'SHAFT'];
        case "fairway":
          return ['FAIRWAY SHAFT','WOOD SHAFT', 'SHAFT'];
        case "hybrid":
          return ['HYBRID SHAFT', 'SHAFT'];
      }
      return [];
    },
    rules: function() {
      return {
        required: value => !!value || "This field is mandatory.",
        notHeadOnlyRequired: value => {
          if (this.model.headOnly) return true;
          return !!value || "This field is mandatory.";
        }
      };
    }
  },
  methods: {
    add() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.$store
          .dispatch("prescriptionWood/add", {
            id: this.prescriptionId,
            model: this.model
          })
          .then(() => {
              this.cancel();
              this.$emit("update");
          })
          .catch((result) => {
            this.message = [];
              for (const field in result.message) {
                if ({}.propertyIsEnumerable.call(result.message, field)) {
                  const msg = result.message[field];
                  this.message.push(msg[0]);
                }
              }
          })
          .finally(() => (this.loading = false));
      }
    },
    update() {
      if (this.$refs.form.validate()) {
        this.loading = true;
        this.$store
          .dispatch("prescriptionWood/update", {
            prescriptionId: this.prescriptionId,
            id: this.model.id,
            model: this.model
          })
          .then(() => {
            this.cancel();
            this.$emit("update");
          })
          .catch((result) => {
            this.message = [];
              for (const field in result.message) {
                if ({}.propertyIsEnumerable.call(result.message, field)) {
                  const msg = result.message[field];
                  this.message.push(msg[0]);
                }
              }
          })
          .finally(() => (this.loading = false));
      }
    },
    cancel() {
      this.resetForm();
      this.displayForm = false
    },
    resetForm() {
      Object.assign(this.model, defaultModel)
      this.$refs.form.reset();
    },
    open (item) {
      const tmp = {}
      if(item) {
        Object.assign(tmp, item)
      }else{
        Object.assign(tmp, defaultModel)
      }
      this.$set(this, 'model', tmp)
      this.displayForm = true
    }
  },
};
</script>

<style lang="less" scoped></style>
